import type { NormalizedCacheObject} from '@apollo/client';
import {
	ApolloClient,
	ApolloLink,
	ApolloProvider,
	createHttpLink,
	from,
	fromPromise,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { cache } from './cache';
import { PLATFORM_NAME } from 'constants/common/PlatformName';
import HttpStatus from 'constants/http';
import { REFRESH_USER_ACCESS_TOKEN_V2 } from 'entites/user/refresh-user-access-token.graphql';
import { handleLogOut } from 'utils/helpers/log-in-log-out/handleLogOut';
import type { FC } from 'react';
import Cookies from 'js-cookie';
import { API_V2 } from 'constants/graphql';

// eslint-disable-next-line prefer-const
export let client: ApolloClient<NormalizedCacheObject>;

const httpLink = createHttpLink({
	uri: process.env.REACT_APP_API_GRAPHQL_URL,
	credentials: 'include',
});

const httpLinkV2 = createHttpLink({
	uri: process.env.REACT_APP_API_GRAPHQL_URL_V2,
	credentials: 'include',
});

const clientNameLink = new ApolloLink((operation, forward) => {
	const context = operation.getContext();
	operation.setContext({
		...context,
		clientName: context.clientName || null,
	});
	return forward(operation);
});

export const getNewToken = () => {
	return client
		.mutate({
			mutation: REFRESH_USER_ACCESS_TOKEN_V2,
			context: {
				clientName: API_V2,
			},
		})
		.then((response) => {
			return response.data?.refreshUserAccessToken;
		});
};

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
	if (graphQLErrors) {
		const notAuthorized = graphQLErrors.some(
			({ extensions }) => {
				if (extensions.originalError) {
					console.log('extensions.originalError', extensions.originalError);
					return (extensions.originalError as any).statusCode === HttpStatus.NOT_AUTHORIZED;
				} else {
					return false;
				}
			}
		);

		if (notAuthorized) {
			const user_id = Cookies.get('user_id') ?? '';

			if (!user_id) {
				handleLogOut();
			}

			return fromPromise(
				getNewToken()
					.then(data => {
						return data;
					})
					.catch(() => {
						handleLogOut();
					}),
			)
				.flatMap(() => forward(operation));
		} else {
			return undefined;
		}
	} else {
		return undefined;
	}
});

// 🔗 Розділяємо запити за clientName
const splitLink = ApolloLink.split(
	operation => operation.getContext()?.clientName === API_V2,
	from([httpLinkV2]),
	from([errorLink, httpLink]),
);

client = new ApolloClient({
	link: ApolloLink.from([clientNameLink, splitLink]),
	cache: cache,
	name: PLATFORM_NAME,
});



type Props = {
	children: React.ReactNode | JSX.Element | JSX.Element[];
};

const WithApollo: FC<Props> = ({ children }) => (
	<ApolloProvider client={client}>
		{children}
	</ApolloProvider>
);

export default WithApollo;
